<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from "vue";
  import { itemListByZoneWithCursorApi, type Product } from "@src/api";
  import { useSkeleton } from "@ui-kit";
  import { useProfileOptions } from "@src/hooks";
  import { type ShopZoneWithChildren } from "./useProductZoning";
  import { default as ProductZoneGroupItem } from "./ProductZoneGroupItem.vue";
  import { default as ProductZoningDeleteModal } from "./ProductZoningDeleteModal.vue";
  import { default as ProductZoneGroup } from "./ProductZoneGroup.vue";
  import { ZoningSkeleton } from "./ui";

  const { shopZone, productList } = defineProps<{ shopZone: ShopZoneWithChildren; productList: Product[] }>();
  const emit = defineEmits<{ "click:delete": [value: Product] }>();

  const productForDelete = ref<Product | null>(null);

  const { hasPermission } = useProfileOptions();
  const { isSkeletonVisible, setSkeletonOn, setSkeletonOff } = useSkeleton();

  const groupedItems = computed(() => {
    if (!productList) {
      return {
        moreThan14Days: [],
        moreThan7Days: [],
        recent: [],
      };
    }

    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

    const moreThan14Days: typeof productList = [];
    const moreThan7Days: typeof productList = [];
    const recent: typeof productList = [];

    productList.forEach((item) => {
      const updatedDate = item.link_created ? new Date(item.link_created) : new Date();

      if (updatedDate < fourteenDaysAgo) {
        moreThan14Days.push(item);
      } else if (updatedDate < sevenDaysAgo) {
        moreThan7Days.push(item);
      } else {
        recent.push(item);
      }
    });

    // Сортировка по артикулу в каждой группе
    moreThan14Days.sort((a, b) => a.article.localeCompare(b.article));
    moreThan7Days.sort((a, b) => a.article.localeCompare(b.article));
    recent.sort((a, b) => a.article.localeCompare(b.article));

    return {
      moreThan14Days,
      moreThan7Days,
      recent,
    };
  });

  onMounted(async () => {
    setSkeletonOn();
    const map = new Map<string, Product>();

    productList.forEach((product) => {
      map.set(product.item_id, product);
    });

    const item_list = await itemListByZoneWithCursorApi(shopZone.zone_id);

    item_list.forEach((product) => {
      if (!map.has(product.item_id)) {
        map.set(product.item_id, product);
        productList.push(product);
      }
    });

    setSkeletonOff();
  });

  onUnmounted(() => {
    productList.splice(0, productList.length);
  });
</script>

<template>
  <ZoningSkeleton v-if="isSkeletonVisible" />
  <ProductZoneGroup v-if="groupedItems.moreThan14Days.length > 0" :title="$t('Привязаны более 14 дней назад')">
    <ProductZoneGroupItem
      v-for="item in groupedItems.moreThan14Days"
      :key="item.item_id"
      :image="item.image_url"
      :name="item.title"
      :article="item.article"
      :color="item.color"
      :link_created="item.link_created"
      color_date_link="error"
      :visible_delete_button="hasPermission('mobile.item_unset_zone')"
      @click:delete="productForDelete = item"
      data-testid="product-zoning-product-item"
    />
  </ProductZoneGroup>
  <ProductZoneGroup v-if="groupedItems.moreThan7Days.length > 0" :title="$t('Привязаны более 7 дней назад')">
    <ProductZoneGroupItem
      v-for="item in groupedItems.moreThan7Days"
      :key="item.item_id"
      :image="item.image_url"
      :name="item.title"
      :article="item.article"
      :color="item.color"
      :link_created="item.link_created"
      color_date_link="warning"
      :visible_delete_button="hasPermission('mobile.item_unset_zone')"
      @click:delete="productForDelete = item"
      data-testid="product-zoning-product-item"
    />
  </ProductZoneGroup>
  <ProductZoneGroup v-if="groupedItems.recent.length > 0" :title="$t('Привязаны менее 7 дней назад')">
    <ProductZoneGroupItem
      v-for="item in groupedItems.recent"
      :key="item.item_id"
      :image="item.image_url"
      :name="item.title"
      :article="item.article"
      :color="item.color"
      :link_created="item.link_created"
      :visible_delete_button="hasPermission('mobile.item_unset_zone')"
      @click:delete="productForDelete = item"
      data-testid="product-zoning-product-item"
    />
  </ProductZoneGroup>
  <ProductZoningDeleteModal
    :productId="productForDelete?.item_id"
    :zoneId="shopZone.zone_id"
    @click:close="productForDelete = null"
    @click:delete="emit('click:delete', productForDelete)"
  />
</template>
